import { cloneDeep, get, isUndefined } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    MaterialIdsType,
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';
import type {
    BackgroundAdjustPayload,
    EffectAdjustInfo,
    GlowAdjustPayload,
    ShadowAdjustPayload,
    StrokeAdjustPayload,
    TextEffectKind,
    TextEffectPreset,
} from '../types';
import {
    EffectCreator,
    FeatureChecker,
    pushHistory,
    updateGroupLayer,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { type IShapeText } from './shape-text';

export interface ITextEffect {
    /**
     * 设置描边特效
     * @param layerID - 图层 id
     * @returns
     */
    addStroke(layerID: string, options?: { strokeColor?: string }): Promise<ILayerProps | void>;
    /**
     * 设置阴影特效
     * @param layerID - 图层 id
     * @returns
     */
    addShadow(layerID: string, options?: { shadowColor?: string }): Promise<ILayerProps | void>;
    /**
     * 设置外发光特效
     * @param layerID - 图层 id
     * @returns
     */
    addGlow(layerID: string, options?: { glowColor?: string }): Promise<ILayerProps | void>;
    /**
     * 设置背景特效
     * @param layerID - 图层 id
     * @returns
     */
    addBackground(layerID: string, options?: { backgroundColor?: string }): Promise<ILayerProps | void>;
    /**
     * 设置指定特效的显隐
     * @param layerID - 图层 id
     * @param effect - 特效类型
     * @param visible - 显隐状态
     * @param index - 特效索引
     * @returns
     */
    setEffectVisibility(
        layerID: string,
        effect: TextEffectKind,
        flag: boolean,
        index?: number,
    ): Promise<ILayerProps | void>;
    /**
     * 移除指定特效
     * @param layerID - 图层 id
     * @param effect - 特效类型
     * @param index - 特效索引
     * @returns
     */
    removeEffect(layerID: string, effect: TextEffectKind, index?: number): Promise<ILayerProps | void>;
    /**
     * 移除特效
     * @param layerID - 图层 id
     * @returns
     */
    clearAllEffects(layerID: string): Promise<ILayerProps | void>;
    /**
     * 应用特效预设
     * @param layerID - 图层 id
     * @param presetID - 特效预设ID
     * @returns
     */
    applyEffectPreset(layerID: string, preset: TextEffectPreset): Promise<ILayerProps | void>;
    /**
     * 调整描边特效属性
     * @param layerID - 图层 id
     * @param payload - 调整信息
     * @param options - 配置项
     * @returns
     */
    adjustStrokeProps(
        layerID: string,
        payload: StrokeAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;
    /**
     * 调整阴影特效属性
     * @param layerID - 图层 id
     * @param payload - 调整信息
     * @param options - 配置项
     * @returns
     */
    adjustShadowProps(
        layerID: string,
        payload: ShadowAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;
    /**
     * 调整外发光特效属性
     * @param layerID - 图层 id
     * @param payload - 调整信息
     * @param options - 配置项
     * @returns
     */
    adjustGlowProps(
        layerID: string,
        payload: GlowAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;
    /**
     * 调整背景特效属性
     * @param layerID - 图层 id
     * @param payload - 调整信息
     * @param options - 配置项
     * @returns
     */
    adjustBackgroundProps(
        layerID: string,
        payload: BackgroundAdjustPayload,
        options?: { writeHistory?: boolean; clearPrevLayer?: boolean },
    ): Promise<ILayerProps | void>;
}

export class TextEffect implements ITextEffect {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private static macCountDict: Record<TextEffectKind, number> = {
        textStroke: 3,
        textShadow: 1,
        textGlow: 1,
        textBackground: 1,
    };

    private static dataUpdateWays: Record<TextEffectKind, 'push' | 'replace'> = {
        textStroke: 'push',
        textShadow: 'replace',
        textGlow: 'replace',
        textBackground: 'replace',
    };

    private createEffect(effect: TextEffectKind, fontSize: number, color?: string) {
        switch (effect) {
            case 'textStroke':
                return EffectCreator.createStroke(fontSize, color);
            case 'textShadow':
                return EffectCreator.createShadow(fontSize, color);
            case 'textGlow':
                return EffectCreator.createGlow(fontSize, color);
            case 'textBackground':
                return EffectCreator.createBackground(fontSize, color);
        }
    }

    /**
     * 解决冲突
     *  - 背景特效和划重点冲突
     * @param layer - 图层
     * @param effect - 特效类型
     * @returns
     */
    private resolveConflict(layer: ILayerProps, effect: TextEffectKind) {
        if (effect !== 'textBackground') return layer;

        const layerData = layer.data as ITextLayerProps;
        const { textMark, textContents = [] } = layerData;

        const nextLayerData: ITextLayerProps = { ...layerData };
        const nextMaterialIds = cloneDeep(layer.materialIds || {});

        if (textMark) {
            nextLayerData.textMark = undefined;
            Reflect.deleteProperty(nextMaterialIds, MaterialIdsType.TEXT_MARK);
        }

        if (textContents.length > 0) {
            const nextTextContents = textContents.map((text) => ({
                ...text,
                textMark: undefined,
            }));
            nextLayerData.textContents = nextTextContents;
        }

        const nextLayer: ILayerProps = { ...layer, data: nextLayerData, materialIds: nextMaterialIds };
        return nextLayer;
    }

    private async add(
        prevLayer: ILayerProps,
        effect: TextEffectKind,
        options: { activate?: boolean; writeHistory?: boolean; color?: string } = {},
    ) {
        const { activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevEffect = prevLayerData[effect] || [];

        if (prevEffect.length === TextEffect.macCountDict[effect]) return;

        const nextEffectItem = this.createEffect(effect, prevLayerData.fontSize, options?.color);
        const updateWay = TextEffect.dataUpdateWays[effect];

        let nextEffect = [...prevEffect];

        if (updateWay === 'push') {
            nextEffect.push(nextEffectItem);
        } else if (updateWay === 'replace') {
            nextEffect = [nextEffectItem];
        }

        const nextLayerData: ITextLayerProps = { ...prevLayerData, [effect]: nextEffect };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };
        nextLayer = this.resolveConflict(nextLayer, effect);

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][effect]);

        return nextLayer;
    }

    private async addEffect(layerID: string, effect: TextEffectKind, options?: { color?: string }) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.add(child, effect, { ...options, activate: false })),
            );
            let layers = results.filter(Boolean) as ILayerProps[];

            // 产品要求在批量添加描边时，需要将所有图层最新添加的描边颜色设置为一致（添加单个描边时，描边的颜色为随机计算）
            if (layers.length > 0 && effect === 'textStroke') {
                const firstLayerStrokes = get(layers[0], 'data.textStroke', []);
                const lastStroke = firstLayerStrokes[firstLayerStrokes.length - 1];

                if (lastStroke) {
                    const color = lastStroke.color;
                    layers = layers.map((layer, idx) => {
                        if (idx === 0) return layer;

                        const layerData = layer.data as ITextLayerProps;
                        const strokes = layerData.textStroke || [];
                        if (strokes.length === 0) return layer;

                        const nextStrokes = strokes.map((stroke, strokeIdx) => {
                            if (strokeIdx !== strokes.length - 1) return stroke;
                            return { ...stroke, color };
                        });
                        const nextLayerData: ITextLayerProps = { ...layerData, textStroke: nextStrokes };
                        const nextLayer: ILayerProps = { ...layer, data: nextLayerData };
                        return nextLayer;
                    });
                }
            }

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER][effect],
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.add(prevLayer, effect, { activate: true, writeHistory: true, color: options?.color });
    }

    addStroke(layerID: string, options?: { strokeColor?: string }) {
        return this.addEffect(layerID, 'textStroke', { color: options?.strokeColor });
    }

    addShadow(layerID: string, options?: { shadowColor?: string }) {
        return this.addEffect(layerID, 'textShadow', { color: options?.shadowColor });
    }

    addGlow(layerID: string, options?: { glowColor?: string }) {
        return this.addEffect(layerID, 'textGlow', { color: options?.glowColor });
    }

    addBackground(layerID: string, options?: { backgroundColor?: string }) {
        return this.addEffect(layerID, 'textBackground', { color: options?.backgroundColor });
    }

    private async remove(
        prevLayer: ILayerProps,
        effect: TextEffectKind,
        options: { index?: number; activate?: boolean; writeHistory?: boolean },
    ) {
        const { index, activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevEffect = (prevLayerData[effect] || []) as Array<
            IFontStroke | IFontShadow | IFontGlow | IFontBackground
        >;

        if (prevEffect.length === 0 || (index && index >= prevEffect.length)) return;

        const nextEffect = prevEffect.filter((_, i) => {
            if (isUndefined(index)) return false;
            return i !== index;
        });
        const nextLayerData: ITextLayerProps = { ...prevLayerData, [effect]: nextEffect };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][effect]);

        return nextLayer;
    }

    async removeEffect(layerID: string, effect: TextEffectKind, index) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.remove(child, effect, { index, activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.GROUP_LAYER][effect],
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.remove(prevLayer, effect, { index, activate: true, writeHistory: true });
    }

    private async clear(prevLayer: ILayerProps, options: { activate?: boolean; writeHistory?: boolean } = {}) {
        const { activate = true, writeHistory = false } = options;

        const hasNoEffects = FeatureChecker.hasNoEffects(prevLayer);
        const isUsingGradient = FeatureChecker.usingGradient(prevLayer);

        if (hasNoEffects && !isUsingGradient) return;

        const DEFAULT_COLOR = '#000000ff';

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextTextContents = (() => {
            if (!prevLayerData.textContents || prevLayerData.textContents.length === 0) return [];
            return prevLayerData.textContents.map((item) => ({
                ...item,
                color: DEFAULT_COLOR,
            }));
        })();

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            color: DEFAULT_COLOR,
            textGradient: null,
            textStroke: [],
            textShadow: [],
            textGlow: [],
            textBackground: [],
            textContents: nextTextContents,
        };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        const prevMaterialIds = prevLayer.materialIds || {};
        const nextMaterialIds = cloneDeep(prevMaterialIds);
        delete nextMaterialIds[MaterialIdsType.TEXT_EFFECT];

        nextLayer = { ...nextLayer, materialIds: nextMaterialIds };

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer);

        return nextLayer;
    }

    async clearAllEffects(layerID: string) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.clear(child, { activate: false, writeHistory: true })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.clear(prevLayer, { activate: true, writeHistory: true });
    }

    private async setVisibility(
        prevLayer: ILayerProps,
        effect: TextEffectKind,
        flag: boolean,
        options: { index?: number; activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { index, activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevEffect = (prevLayerData[effect] || []) as Array<
            IFontStroke | IFontShadow | IFontGlow | IFontBackground
        >;

        if (prevEffect.length === 0 || (index && index >= prevEffect.length)) return;

        const nextEffect = prevEffect.map((item, i) => {
            if (i === index || isUndefined(index)) {
                return { ...item, hidden: flag };
            }
            return item;
        });
        const nextLayerData: ITextLayerProps = { ...prevLayerData, [effect]: nextEffect };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][effect]);

        return nextLayer;
    }

    async setEffectVisibility(layerID: string, effect: TextEffectKind, flag: boolean, index: number) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.setVisibility(child, effect, flag, { index, activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.setVisibility(prevLayer, effect, flag, { index, writeHistory: true, activate: true });
    }

    private async applyPreset(
        prevLayer: ILayerProps,
        preset: TextEffectPreset,
        options: { activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const { materialID, color, textStroke, textGlow, textShadow, textBackground, textGradient = null } = preset;

        const nextTextContents = (() => {
            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                return prevTextContents.map((item) => ({
                    ...item,
                    color,
                    underline: textGradient ? false : item.underline,
                    lineThrough: textGradient ? false : item.lineThrough,
                }));
            }
            return [];
        })();

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            textContents: nextTextContents,
            color,
            textStroke,
            textGlow,
            textShadow,
            textBackground,
            textGradient,
            textMark: textBackground.length > 0 ? null : prevLayerData.textMark, // 背景与文字划重点互斥
            lineThrough: textGradient ? false : prevLayerData.lineThrough,
            underline: textGradient ? false : prevLayerData.underline,
            texture: undefined,
        };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const prevMaterialIds = prevLayer.materialIds || {};
        const nextMaterialIds = cloneDeep(prevMaterialIds);

        if (materialID) {
            nextMaterialIds[MaterialIdsType.TEXT_EFFECT] = [String(materialID)];
        } else {
            delete nextMaterialIds[MaterialIdsType.TEXT_EFFECT];
        }

        nextLayer = { ...nextLayer, materialIds: nextMaterialIds };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);

        return nextLayer;
    }

    async applyEffectPreset(layerID: string, preset: TextEffectPreset) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.applyPreset(child, preset, { activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].effects,
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.applyPreset(prevLayer, preset, { activate: true, writeHistory: true });
    }

    private async adjustProps(
        prevLayer: ILayerProps,
        info: EffectAdjustInfo,
        options: { activate?: boolean; writeHistory?: boolean } = {},
    ) {
        // https://jira.meitu.com/browse/MTXXPRO-59636
        const { activate = true, writeHistory = false } = options;

        const { target, payload } = info;
        const { idx, data } = payload;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const prevEffect = prevLayerData[target] as Array<IFontStroke | IFontShadow | IFontGlow | IFontBackground>;
        const prevEffectItem = prevEffect[idx];

        if (!prevEffectItem) return;

        const nextEffectItem = { ...prevEffectItem, ...data };
        const nextEffect = prevEffect.map((item, i) => (i === idx ? nextEffectItem : item));
        const nextLayerData: ITextLayerProps = { ...prevLayerData, [target]: nextEffect };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const shapeExtension = get(prevLayer, 'data.shapeExtension', null);
        if (shapeExtension && writeHistory) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;

            updateLayer(this.framework, nextLayer);
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].effects);

        return nextLayer;
    }

    private async adjustEffectProps(
        layerID: string,
        info: EffectAdjustInfo,
        options = { writeHistory: false, clearPrevLayer: false },
    ) {
        const { writeHistory, clearPrevLayer } = options;

        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.adjustProps(child, info, { activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].effects,
                update: !writeHistory,
                writeHistory,
            });

            if (writeHistory) this.prevLayer = null;

            return nextLayer;
        }

        const nextLayer = await this.adjustProps(prevLayer, info, { activate: true, writeHistory });
        if (writeHistory || clearPrevLayer) this.prevLayer = null;

        return nextLayer;
    }

    async adjustStrokeProps(
        layerID: string,
        payload: StrokeAdjustPayload,
        options = { writeHistory: true, clearPrevLayer: false },
    ) {
        const info: EffectAdjustInfo = { target: 'textStroke', payload };
        return this.adjustEffectProps(layerID, info, options);
    }

    async adjustShadowProps(
        layerID: string,
        payload: ShadowAdjustPayload,
        options = { writeHistory: true, clearPrevLayer: false },
    ) {
        const info: EffectAdjustInfo = { target: 'textShadow', payload };
        return this.adjustEffectProps(layerID, info, options);
    }

    async adjustGlowProps(
        layerID: string,
        payload: GlowAdjustPayload,
        options = { writeHistory: true, clearPrevLayer: false },
    ) {
        const info: EffectAdjustInfo = { target: 'textGlow', payload };
        return this.adjustEffectProps(layerID, info, options);
    }

    async adjustBackgroundProps(
        layerID: string,
        payload: BackgroundAdjustPayload,
        options = { writeHistory: true, clearPrevLayer: false },
    ) {
        const info: EffectAdjustInfo = { target: 'textBackground', payload };
        return this.adjustEffectProps(layerID, info, options);
    }
}
